import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { ArrowRight } from "lucide-react"
import { Link } from "react-router-dom"
import { VideoBackground } from "@/components/ui/VideoBackground"
import { SEO } from "@/components/SEO"

const services = [
    {
        num: "01",
        title: "AI Transformation & Automation",
        subtitle: "Future-Proofing Business",
        description:
            "Strategic roadmaps and implementation of Generative AI, LLMs, and neural networks to create autonomous, self-learning enterprise capabilities.",
        link: "/what-we-do/ai-transformation-automation",
        tags: ["Generative AI", "LLMs", "Neural Networks", "Autonomous Systems"],
    },
    {
        num: "02",
        title: "Managed Services & AIOps",
        subtitle: "Proactive Management",
        description:
            "Proactive AI-driven monitoring and management ensuring 99.99% uptime and self-healing infrastructure.",
        link: "/what-we-do/managed-services-aiops",
        tags: ["AIOps", "99.99% Uptime", "Self-Healing", "Monitoring"],
    },
    {
        num: "03",
        title: "Cloud & Infrastructure",
        subtitle: "Scalable Foundations",
        description:
            "Architecting resilient, scalable cloud environments and edge computing networks that support high-velocity digital transformation.",
        link: "/what-we-do/cloud-infrastructure",
        tags: ["AWS", "Azure", "GCP", "Edge Computing"],
    },
    {
        num: "04",
        title: "Enterprise Systems",
        subtitle: "Operational Excellence",
        description:
            "Modernizing legacy ERP/CRM cores with intelligent automation and custom AI agents to drive efficiency and predictive insights.",
        link: "/what-we-do/cognitive-enterprise",
        tags: ["ERP", "CRM", "Salesforce", "SAP"],
    },
    {
        num: "05",
        title: "Generative AI Products",
        subtitle: "Next-Gen Engineering",
        description:
            "Engineering next-gen software infused with advanced generative AI capabilities and self-learning loops.",
        link: "/what-we-do/generative-ai",
        tags: ["LeoRix AI", "Connexr", "Custom AI", "ML Ops"],
    },
    {
        num: "06",
        title: "Need a Custom Solution?",
        subtitle: "Tailored For You",
        description:
            "Our experts can design a tailored roadmap for your unique challenges. Let's build something extraordinary together.",
        link: "/contact",
        tags: ["Strategy", "Advisory", "Custom Roadmap"],
        isSpecial: true,
    },
]

const engagementSteps = [
    {
        num: "01",
        title: "Assess & Align",
        description: "We analyze your current infrastructure, identify AI opportunities, and align technology roadmaps with business goals.",
    },
    {
        num: "02",
        title: "Design & Modernize",
        description: "We re-architect legacy systems, deploy cloud-native foundations, and integrate intelligent automation layers.",
    },
    {
        num: "03",
        title: "Operate & Optimize",
        description: "We provide ongoing managed services, using AIOps to continuously monitor, self-heal, and optimize your environment.",
    },
]

const Services = () => {
    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="From Strategy to Continuous Innovation | RSA Tech Group Services"
                description="We go beyond managing technology. We help enterprises modernize, operate, and continuously improve intelligent systems that create long-term business value."
                image="https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2672&auto=format&fit=crop"
            />
            <Navigation />

            {/* Hero Section */}
            <div className="relative pt-48 pb-24 overflow-hidden bg-[#0f1629]">
                <VideoBackground videoSrc="/Home%20Hero%20page.mp4" />
                <div className="absolute inset-0 bg-[#0f1629]/70 pointer-events-none z-[1]" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_30%,rgba(28,43,110,0.25),transparent_60%)] z-[1]" />
                <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#FDA400]/[0.04] rounded-full blur-[120px] z-[1]" />

                <div className="section-container relative z-10 text-center">
                    <div className="max-w-4xl mx-auto">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-6">
                            What We Do
                        </p>
                        <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-[1.1] tracking-tight">
                            From Strategy to{" "}
                            <span className="text-[#FDA400]">Continuous Innovation</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto mb-12 font-light leading-relaxed">
                            We go beyond managing technology. We help enterprises modernize, operate, and continuously improve intelligent systems that create long-term business value.
                        </p>
                        <Link
                            to="/contact"
                            className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-[#1C2B6E] bg-[#FDA400] rounded-full hover:bg-[#f5b800] transition-all duration-300 shadow-[0_8px_30px_rgba(253,164,0,0.3)] hover:shadow-[0_12px_40px_rgba(253,164,0,0.4)] hover:-translate-y-0.5"
                        >
                            Talk to our Experts <ArrowRight className="ml-2 h-5 w-5" />
                        </Link>
                    </div>
                </div>
            </div>

            {/* Services Overview */}
            <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
                {/* Dot grid bg */}
                <div
                    className="absolute inset-0 opacity-[0.25]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />
                <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-[#1C2B6E]/[0.02] rounded-full blur-[150px]" />
                <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#FDA400]/[0.02] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    <div className="text-center mb-20">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Our Capabilities
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
                            RSA Tech Group{" "}
                            <span className="text-[#FDA400]">Solutions</span>
                        </h2>
                        <p className="text-slate-500 text-lg mt-6 max-w-3xl mx-auto font-light leading-relaxed">
                            Comprehensive AI-native solutions designed to modernize, scale, and secure your enterprise through engineering excellence.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
                        {services.map((service, index) => (
                            <Link
                                key={service.title}
                                to={service.link}
                                className={`partner-card group rounded-3xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_25px_80px_rgba(28,43,110,0.12)] transition-all duration-500 hover:-translate-y-2 block text-left ${
                                    index === 0 ? "lg:col-span-2" : ""
                                }`}
                            >
                                {/* Shine sweep */}
                                <div className="absolute inset-0 z-20 overflow-hidden rounded-3xl pointer-events-none">
                                    <div className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-[shine_0.8s_ease-in-out]" />
                                </div>

                                {/* Top accent */}
                                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FDA400] to-[#f5b800] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-10" />

                                {/* Glow */}
                                <div className="absolute -bottom-12 -right-12 w-44 h-44 bg-[#FDA400]/[0.04] rounded-full blur-[50px] group-hover:bg-[#FDA400]/10 group-hover:scale-110 transition-all duration-700" />

                                {/* Ghost number */}
                                <span className="absolute top-4 right-6 text-8xl font-black text-[#1C2B6E]/[0.03] leading-none select-none group-hover:text-[#1C2B6E]/[0.07] transition-colors duration-500">
                                    {service.num}
                                </span>

                                <div className="p-8 md:p-10 h-full flex flex-col relative z-10">
                                    {/* Number label */}
                                    <div className="text-xs font-bold text-[#FDA400] uppercase tracking-[0.2em] mb-5">
                                        {service.num} / {service.subtitle}
                                    </div>

                                    <h3 className="text-2xl md:text-3xl font-black text-[#1C2B6E] tracking-tight mb-3 group-hover:text-[#1C2B6E] transition-colors">
                                        {service.title}
                                    </h3>

                                    <p className="text-slate-500 text-sm leading-relaxed font-light mb-6">
                                        {service.description}
                                    </p>

                                    {/* Tech tags */}
                                    <div className="flex flex-wrap gap-2 mt-auto pt-6 border-t border-slate-100">
                                        {service.tags.map((tag, i) => (
                                            <span
                                                key={i}
                                                className="px-3 py-1.5 bg-[#1C2B6E]/[0.04] text-[#1C2B6E]/70 text-xs font-medium rounded-full border border-[#1C2B6E]/[0.06] hover:bg-[#FDA400]/10 hover:border-[#FDA400]/30 hover:text-[#FDA400] transition-all duration-300 cursor-default"
                                            >
                                                {tag}
                                            </span>
                                        ))}
                                        <span className="ml-auto text-[#FDA400] text-sm font-bold flex items-center gap-2 group-hover:gap-3 transition-all duration-300">
                                            {service.isSpecial ? "Talk to an Expert" : "Explore"} <ArrowRight className="h-4 w-4" />
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Engagement Model */}
            <section className="py-24 relative overflow-hidden bg-[#0f1629]">
                <div
                    className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
                    style={{ backgroundImage: "url('/engagement_process_bg.png')" }}
                >
                    <div className="absolute inset-0 bg-[#0f1629]/85" />
                </div>
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(28,43,110,0.15),transparent_70%)]" />
                <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#FDA400]/[0.03] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    <div className="text-center mb-20">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Our Process
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1]">
                            How We{" "}
                            <span className="text-[#FDA400]">Work</span>
                            {" "}With You
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 relative max-w-6xl mx-auto">
                        {/* Connecting Line (Desktop) */}
                        <div className="hidden md:block absolute top-16 left-[20%] right-[20%] h-[2px] bg-gradient-to-r from-[#FDA400]/40 via-[#1C2B6E]/30 to-[#FDA400]/40 z-0" />

                        {engagementSteps.map((step) => (
                            <div key={step.num} className="relative z-10 text-center">
                                <div className="w-28 h-28 mx-auto bg-[#0f1629] border-2 border-[#FDA400]/30 rounded-full flex items-center justify-center mb-8 shadow-[0_0_30px_rgba(253,164,0,0.1)] group">
                                    <span className="text-3xl font-black text-[#FDA400]">{step.num}</span>
                                </div>
                                <h3 className="text-xl font-black mb-4 text-white tracking-tight">
                                    {step.title}
                                </h3>
                                <p className="text-slate-400 text-sm leading-relaxed font-light px-4">
                                    {step.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Executive FAQs */}
            <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
                {/* Dot grid bg */}
                <div
                    className="absolute inset-0 opacity-[0.2]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />
                <div className="absolute top-0 right-1/3 w-[500px] h-[500px] bg-[#1C2B6E]/[0.02] rounded-full blur-[150px]" />

                <div className="section-container relative z-10 max-w-4xl mx-auto">
                    <div className="text-center mb-16">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            FAQ
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
                            Common{" "}
                            <span className="text-[#FDA400]">Questions</span>
                        </h2>
                    </div>

                    <Accordion type="single" collapsible className="w-full space-y-4">
                        <AccordionItem value="item-1" className="border border-[#1C2B6E]/10 bg-white rounded-2xl px-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                            <AccordionTrigger className="text-[#1C2B6E] hover:text-[#FDA400] hover:no-underline text-lg font-bold py-6 tracking-tight">
                                How does RSA Tech Group apply AI in managed services?
                            </AccordionTrigger>
                            <AccordionContent className="text-slate-500 pb-6 text-base font-light leading-relaxed">
                                AI is embedded throughout our delivery chain, starting with predictive monitoring that creates alerts before failures occur and extending to automated remediation agents that resolve routine tickets instantly. This shifts operations from reactive break-fix support to proactive optimization.
                            </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="item-2" className="border border-[#1C2B6E]/10 bg-white rounded-2xl px-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                            <AccordionTrigger className="text-[#1C2B6E] hover:text-[#FDA400] hover:no-underline text-lg font-bold py-6 tracking-tight">
                                Do you work with legacy enterprise systems?
                            </AccordionTrigger>
                            <AccordionContent className="text-slate-500 pb-6 text-base font-light leading-relaxed">
                                Yes, we specialize in hybrid environments. We avoid rip-and-replace approaches unless necessary. Instead, we build intelligent wrappers and API layers around legacy cores to modernize them incrementally while ensuring business continuity.
                            </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="item-3" className="border border-[#1C2B6E]/10 bg-white rounded-2xl px-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                            <AccordionTrigger className="text-[#1C2B6E] hover:text-[#FDA400] hover:no-underline text-lg font-bold py-6 tracking-tight">
                                How do you ensure data security?
                            </AccordionTrigger>
                            <AccordionContent className="text-slate-500 pb-6 text-base font-light leading-relaxed">
                                Security is native to our design. We operate under strict zero-trust frameworks and utilize private AI models that never train on your data without permission. We maintain compliance with global standards including GDPR, HIPAA, and SOC2.
                            </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="item-4" className="border border-[#1C2B6E]/10 bg-white rounded-2xl px-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                            <AccordionTrigger className="text-[#1C2B6E] hover:text-[#FDA400] hover:no-underline text-lg font-bold py-6 tracking-tight">
                                What makes you different from a standard MSP?
                            </AccordionTrigger>
                            <AccordionContent className="text-slate-500 pb-6 text-base font-light leading-relaxed">
                                Traditional MSPs focus on keeping the lights on through manual labor. We focus on making the lights brighter through intelligent automation. Our approach is engineering-led rather than ticket-driven, allowing us to continuously code away operational toil.
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-28 relative overflow-hidden bg-[#0f1629]">
                <div
                    className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
                    style={{ backgroundImage: "url('/services_cta_premium_bg.png')" }}
                >
                    <div className="absolute inset-0 bg-[#0f1629]/80" />
                </div>
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(28,43,110,0.2),transparent_70%)]" />
                <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-[#FDA400]/[0.04] rounded-full blur-[150px]" />

                <div className="section-container relative z-10 text-center">
                    <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-6">
                        Get Started
                    </p>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1] mb-6">
                        Ready to Future-Proof{" "}
                        <span className="text-[#FDA400]">Your Operations?</span>
                    </h2>
                    <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto font-light leading-relaxed">
                        Schedule a consultation to discover how our AI-first managed services can reduce costs and accelerate innovation.
                    </p>
                    <Link
                        to="/contact"
                        className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-[#1C2B6E] bg-[#FDA400] rounded-full hover:bg-[#f5b800] transition-all duration-300 shadow-[0_8px_30px_rgba(253,164,0,0.3)] hover:shadow-[0_12px_40px_rgba(253,164,0,0.4)] hover:-translate-y-0.5"
                    >
                        Talk to Our Experts <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    )
}

export default Services
